<!--
 * Path: packages/frontend/src/features/chat/FloatingPanelChatbot.vue
 * Task: Floating panel providing an AI chat interface to query project data via Gemini.
 * Update: Uses internal, lightweight markdown parsing to prevent compiler crashes.
-->
<template>
  <FloatingPanelMain
    v-model="modelValue"
    title="Unravel AI"
    width="380px"
    height="500px"
    :resizable="true"
  >
    <div class="d-flex flex-column h-100 position-relative w-100" style="min-width: 0;">

      <!-- Chat Message Area -->
      <div class="flex-grow-1 overflow-y-auto px-4 py-3 custom-scrollbar" ref="chatScrollContainer" style="min-height: 0;">

        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="d-flex flex-column align-center justify-center h-100 text-center text-medium-emphasis">
          <v-icon size="48" color="primary" class="mb-4 opacity-80">mdi-robot-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold mb-2" style="color: rgb(var(--v-theme-hud-text)) !important;">Hello! I am Unravel AI.</div>
          <div class="text-caption mb-4" style="color: rgb(var(--v-theme-hud-text-secondary)) !important;">
            I have analyzed the current project data. Ask me anything about dependencies, orphaned tasks, or process structures.
          </div>
        </div>

        <!-- Messages List -->
        <div class="d-flex flex-column" style="gap: 16px;">
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="d-flex w-100"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <!-- Assistant Avatar -->
            <v-avatar v-if="msg.role === 'assistant'" size="28" color="primary" class="me-2 mt-1 flex-shrink-0">
              <v-icon color="white" size="16">mdi-robot-outline</v-icon>
            </v-avatar>

            <!-- Message Bubble -->
            <div
              class="pa-3 rounded-lg text-body-2 markdown-chat"
              :class="msg.role === 'user' ? 'bg-primary text-white ml-10' : 'bg-surface-variant text-high-emphasis mr-4'"
              style="max-width: 85%; word-break: break-word;"
              v-html="renderMarkdown(msg.text)"
            ></div>
          </div>
        </div>

        <!-- Loading Indicator -->
        <div v-if="isLoading" class="d-flex justify-start mt-4">
          <v-avatar size="28" color="primary" class="me-2 flex-shrink-0">
            <v-icon color="white" size="16">mdi-robot-outline</v-icon>
          </v-avatar>
          <div class="pa-3 rounded-lg bg-surface-variant d-flex align-center" style="height: 36px;">
            <v-progress-circular indeterminate color="primary" size="16" width="2"></v-progress-circular>
          </div>
        </div>
      </div>

      <!-- Input Area (Fixed at bottom) -->
      <div class="flex-shrink-0 px-3 py-3 border-t" style="background-color: rgba(var(--v-theme-hud-background), 0.95); border-color: rgba(var(--v-theme-hud-border), 0.15) !important;">
        <v-text-field
          v-model="inputQuery"
          placeholder="Ask a question about this project..."
          variant="outlined"
          density="compact"
          hide-details
          :disabled="isLoading || !projectStore.loadedProject"
          @keydown.enter="submitQuery"
          class="chat-input"
        >
          <template #append-inner>
            <v-btn
              icon="mdi-send"
              variant="text"
              color="primary"
              size="small"
              density="comfortable"
              :disabled="!inputQuery.trim() || isLoading"
              @click="submitQuery"
            ></v-btn>
          </template>
        </v-text-field>
        <div v-if="!projectStore.loadedProject" class="text-caption text-error mt-1 ms-1">
          Open a project to chat with AI.
        </div>
      </div>
    </div>
  </FloatingPanelMain>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useProjectStore } from '@/store/project';
import { useAuthStore } from '@/store/auth';
import FloatingPanelMain from '@/core/components/floatingPanel/FloatingPanelMain.vue';
import { GeminiService, type ChatMessage } from '@/core/services/GeminiService';
import { AiExportService } from '@/core/services/AiExportService';

const modelValue = defineModel<boolean>({ default: false });
const projectStore = useProjectStore();
const authStore = useAuthStore();

const inputQuery = ref('');
const messages = ref<ChatMessage[]>([]);
const isLoading = ref(false);
const chatScrollContainer = ref<HTMLElement | null>(null);

// Dependency-free, basic Markdown parser to prevent Vite compiler crashes
const renderMarkdown = (text: string) => {
  if (!text) return '';
  // 1. Sanitize basic HTML tags
  let html = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // 2. Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // 3. Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // 4. Code Blocks (simple inline)
  html = html.replace(/`(.*?)`/g, '<code style="background: rgba(128,128,128,0.2); padding: 2px 4px; border-radius: 4px;">$1</code>');
  // 5. Line breaks
  html = html.replace(/\n/g, '<br>');
  return html;
};

const scrollToBottom = async () => {
  await nextTick();
  if (chatScrollContainer.value) {
    chatScrollContainer.value.scrollTop = chatScrollContainer.value.scrollHeight;
  }
};

const submitQuery = async () => {
  const query = inputQuery.value.trim();
  if (!query || isLoading.value || !projectStore.loadedProject) return;

  // Add user message
  messages.value.push({ role: 'user', text: query });
  inputQuery.value = '';
  scrollToBottom();

  isLoading.value = true;

  try {
    const projectId = projectStore.loadedProject.value;

    // Generate context strictly on-the-fly
    const contextMarkdown = await AiExportService.generateProjectMarkdown(projectId);

    const systemPrompt = `You are Unravel AI, an expert process architecture and dependency management assistant.
You are answering questions about the following project. Analyze the provided Markdown context carefully.
Keep your answers helpful, concise, and formatted using Markdown. If you don't know the answer based on the context, say so.\n\n` + contextMarkdown;

    const responseText = await GeminiService.sendChatMessage(query, messages.value, systemPrompt);

    messages.value.push({ role: 'assistant', text: responseText });
  } catch (error: any) {
    authStore.showNotification(`AI Error: ${error.message}`, 'error');
    messages.value.push({ role: 'assistant', text: `**Error:** Failed to reach Unravel AI. (${error.message})` });
  } finally {
    isLoading.value = false;
    scrollToBottom();
  }
};

// Purge State Awareness: Clear chat when project changes to prevent Data Cross-Contamination
watch(() => projectStore.loadedProject, () => {
  messages.value = [];
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-hud-border), 0.2);
  border-radius: 3px;
}

:deep(.markdown-chat p) {
  margin-bottom: 8px;
}
:deep(.markdown-chat p:last-child) {
  margin-bottom: 0;
}

.chat-input :deep(.v-field__input) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  min-height: 40px !important;
}
.chat-input :deep(.v-field) {
  background-color: rgba(var(--v-theme-surface), 0.05) !important;
  border-radius: 8px !important;
}
</style>
